import { forwardRef, useImperativeHandle, useState } from "react";
import { Text } from "@postenbring/hedwig-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import Translated from "../infrastructure/translation/Translated";
import { CertificateUploadRequest } from "../infrastructure/api/api";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import styles from "./NewCertificatePage.module.css";
import {
  CertificateFileError,
  readFileAsBase64,
  validateCertificateFile,
} from "./certificateFile";
import { CertificateFormHandle, CertificateFormProps } from "./certificateForm";

export const UploadCertificate = forwardRef<
  CertificateFormHandle,
  CertificateFormProps
>(({ displayName, fetcher, clearError }, ref) => {
  const translate = useTranslate();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<CertificateFileError | null>(null);
  const [dragging, setDragging] = useState(false);

  const selectFile = (selected: File | undefined) => {
    if (!selected) return;

    clearError();
    const validationError = validateCertificateFile(selected);
    setFileError(validationError);
    setFile(validationError ? null : selected);
  };

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (!file) {
        setFileError("missing");
        return;
      }

      void (async () => {
        const body: CertificateUploadRequest = {
          certificate: await readFileAsBase64(file),
          displayName,
        };
        fetcher.submit(body, { method: "post", encType: "application/json" });
      })();
    },
  }));

  return (
    <div className={styles.field}>
      <span className={styles.fileLabel}>
        <Translated textKey={"new_certificate.certificate_file"} />
      </span>
      <label
        className={
          dragging
            ? `${styles.dropZone} ${styles.dropZoneActive}`
            : styles.dropZone
        }
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          selectFile(e.dataTransfer.files[0]);
        }}
      >
        <input
          type="file"
          accept=".cer,.crt,.pem,.der"
          className={styles.fileInput}
          onChange={(e) => selectFile(e.target.files?.[0])}
        />
        <FontAwesomeIcon
          icon={faLock}
          className={styles.dropIcon}
          aria-hidden
        />
        <span className={styles.dropTitle}>
          <Translated
            textKey={
              dragging
                ? "new_certificate.drop_here"
                : "new_certificate.drop_hint"
            }
          />
        </span>
        <Text variant="body-small" className={styles.help}>
          <Translated textKey={"new_certificate.file_types"} />
        </Text>
      </label>

      {file && (
        <div className={styles.selectedFile}>
          <FontAwesomeIcon
            icon={faCircleCheck}
            className={styles.selectedFileIcon}
            aria-hidden
          />
          <span className={styles.selectedFileName}>{file.name}</span>
          <button
            type="button"
            className={styles.removeFile}
            onClick={() => {
              setFile(null);
              clearError();
            }}
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden />
            <Translated textKey={"new_certificate.remove_file"} />
          </button>
        </div>
      )}

      {fileError && (
        <p className={styles.error}>
          {translate(`new_certificate.file_error_${fileError}`)}
        </p>
      )}
    </div>
  );
});

UploadCertificate.displayName = "UploadCertificate";
